import React, { useState, useEffect, useContext } from "react";
import { AuthContext } from "../App";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Calendar, Clock, Video, Phone, MessageSquare, CheckCircle, AlertCircle } from "lucide-react";

const Consultation = () => {
  const { token } = useContext(AuthContext);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showBookingForm, setShowBookingForm] = useState(false);
  const [formData, setFormData] = useState({
    counselor_name: "",
    date: "",
    time: "",
    type: "video",
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchBookings();
  }, []);

  const fetchBookings = async () => {
    try {
      const response = await axios.get(`${API}/consultations`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setBookings(response.data);
    } catch (error) {
      toast.error("Failed to load bookings");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await axios.post(`${API}/consultations/book`, formData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Consultation booked successfully!");
      setShowBookingForm(false);
      setFormData({ counselor_name: "", date: "", time: "", type: "video" });
      fetchBookings();
    } catch (error) {
      toast.error("Failed to book consultation");
    } finally {
      setSubmitting(false);
    }
  };

  const counselors = [
    { name: "Dr. Priya Sharma", specialty: "Engineering & Technology" },
    { name: "Mr. Rajesh Kumar", specialty: "Medical & Healthcare" },
    { name: "Ms. Anita Verma", specialty: "Business & Commerce" },
    { name: "Prof. Vikram Singh", specialty: "Arts & Humanities" },
  ];

  const getStatusColor = (status) => {
    return status === "pending" ? "bg-yellow-100 text-yellow-700" : "bg-green-100 text-green-700";
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case "video":
        return <Video className="w-4 h-4" />;
      case "phone":
        return <Phone className="w-4 h-4" />;
      case "chat":
        return <MessageSquare className="w-4 h-4" />;
      default:
        return <Video className="w-4 h-4" />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
        <Navbar />
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <Navbar />
      <div className="max-w-6xl mx-auto px-4 py-8" data-testid="consultation-container">
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold mb-2 flex items-center gap-3">
              <Calendar className="w-10 h-10 text-indigo-500" />
              Live Consultations
            </h1>
            <p className="text-gray-600 text-lg">Book sessions with expert career counselors</p>
          </div>
          <Button onClick={() => setShowBookingForm(!showBookingForm)} data-testid="toggle-booking-form-btn">
            {showBookingForm ? "Cancel" : "Book Consultation"}
          </Button>
        </div>

        {/* Coming Soon Banner */}
        <Card className="mb-8 bg-gradient-to-r from-blue-50 to-purple-50 border-2 border-blue-200">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <AlertCircle className="w-6 h-6 text-blue-500 mt-1" />
              <div>
                <h3 className="font-semibold text-lg mb-1">Video Calling Feature Coming Soon!</h3>
                <p className="text-gray-700">
                  We're working on integrating live video consultations. For now, you can book appointments and we'll reach out via email to schedule your session.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Booking Form */}
        {showBookingForm && (
          <Card className="mb-8" data-testid="booking-form">
            <CardHeader>
              <CardTitle>Book a Consultation</CardTitle>
              <CardDescription>Select your preferred counselor and time slot</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="counselor">Select Counselor</Label>
                  <Select
                    value={formData.counselor_name}
                    onValueChange={(value) => setFormData({ ...formData, counselor_name: value })}
                    required
                  >
                    <SelectTrigger data-testid="counselor-select">
                      <SelectValue placeholder="Choose a counselor" />
                    </SelectTrigger>
                    <SelectContent>
                      {counselors.map((counselor) => (
                        <SelectItem key={counselor.name} value={counselor.name}>
                          {counselor.name} - {counselor.specialty}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="date">Date</Label>
                    <Input
                      id="date"
                      type="date"
                      value={formData.date}
                      onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                      min={new Date().toISOString().split('T')[0]}
                      required
                      data-testid="booking-date-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="time">Time</Label>
                    <Input
                      id="time"
                      type="time"
                      value={formData.time}
                      onChange={(e) => setFormData({ ...formData, time: e.target.value })}
                      required
                      data-testid="booking-time-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="type">Consultation Type</Label>
                  <Select
                    value={formData.type}
                    onValueChange={(value) => setFormData({ ...formData, type: value })}
                  >
                    <SelectTrigger data-testid="consultation-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="video">Video Call</SelectItem>
                      <SelectItem value="phone">Phone Call</SelectItem>
                      <SelectItem value="chat">Chat</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <Button type="submit" className="w-full" disabled={submitting} data-testid="submit-booking-btn">
                  {submitting ? "Booking..." : "Confirm Booking"}
                </Button>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Expert Counselors */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold mb-6">Our Expert Counselors</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {counselors.map((counselor, index) => (
              <Card key={index} className="card-hover" data-testid={`counselor-card-${index}`}>
                <CardContent className="pt-6">
                  <div className="w-16 h-16 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-full mx-auto mb-4 flex items-center justify-center">
                    <span className="text-2xl text-white font-bold">{counselor.name.charAt(0)}</span>
                  </div>
                  <h3 className="font-semibold text-center mb-2">{counselor.name}</h3>
                  <p className="text-sm text-gray-600 text-center">{counselor.specialty}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* My Bookings */}
        <div>
          <h2 className="text-2xl font-bold mb-6">My Bookings</h2>
          {bookings.length === 0 ? (
            <Card data-testid="no-bookings-message">
              <CardContent className="p-12 text-center">
                <Calendar className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 text-lg">No bookings yet. Book your first consultation!</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {bookings.map((booking) => (
                <Card key={booking.id} className="card-hover" data-testid={`booking-${booking.id}`}>
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="font-semibold text-lg">{booking.counselor_name}</h3>
                          <Badge className={getStatusColor(booking.status)}>{booking.status}</Badge>
                        </div>
                        <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                          <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4" />
                            <span>{new Date(booking.date).toLocaleDateString()}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Clock className="w-4 h-4" />
                            <span>{booking.time}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            {getTypeIcon(booking.type)}
                            <span className="capitalize">{booking.type}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Consultation;